import { ReactNode, useState, useEffect } from 'react';
import { Toast } from './Toast';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const ErrorBoundary = ({ children, fallback }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      setError(new Error(event.error.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setHasError(true);
      setError(new Error(event.reason.message));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset error boundary when children change
  useEffect(() => {
    if (hasError) {
      setHasError(false);
      setError(null);
    }
  }, [children]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {children}

      {hasError &&
        (fallback ?? (
          <Toast
            message={error?.message || ''}
            onClose={() => {
              setHasError(false);
              setError(null);
            }}
          />
        ))}
    </>
  );
};
